import { useCallback, useState } from 'react';
import { Link } from 'react-router';
import { PauseCircle, Printer as PrinterIcon, X } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useOrdersSignalR } from '@/features/orders/hooks/useOrdersSignalR';
import { usePrintingStatus } from '../hooks/usePrinting';
import { usePrintingAvailable } from '../hooks/usePrintingAvailable';
import { printingNotice } from '../lib/printing-notice';
import { claimOverdueAlert, describeOverdue, overdueAlertKey } from '../lib/overdue-alerts';
import { playPrintingAlert } from '../lib/printing-alert-sound';
import type { PrintJobUpdatedEvent } from '../types/printing.types';
import styles from './PrintingStatusBar.module.css';

interface OverdueAlert {
  key: string;
  text: string;
  orderId: string;
}

/**
 * Faixa fixa, aviso de pausa e aviso sonoro da impressão, em qualquer página
 * do painel, para quem vê pedidos. Some sozinho quando a loja está fora do
 * piloto: nenhuma leitura, nenhuma conexão.
 */
export function PrintingStatusBar() {
  const { available } = usePrintingAvailable('orders.read');
  if (!available) return null;
  return <PrintingStatusBarContent />;
}

function PrintingStatusBarContent() {
  const permissions = useAuthStore((state) => state.user?.permissions);
  const can = {
    settingsWrite: permissions?.includes('settings.write') ?? false,
    billingRead: permissions?.includes('billing.read') ?? false,
  };

  // Revalidado a cada 60 s, na reconexão e nos eventos `printing.*`, que a
  // conexão compartilhada de pedidos já trata como campainha.
  const { data: status } = usePrintingStatus();
  const [alerts, setAlerts] = useState<OverdueAlert[]>([]);

  const handleJobUpdated = useCallback((event: PrintJobUpdatedEvent) => {
    const key = overdueAlertKey(event);
    if (!key || !event.orderId || !claimOverdueAlert(key)) return;
    playPrintingAlert();
    const alert = { key, text: describeOverdue(event), orderId: event.orderId };
    setAlerts((current) => [alert, ...current].slice(0, 3));
  }, []);

  useOrdersSignalR({ onPrintingJobUpdated: handleJobUpdated });

  const notice = status ? printingNotice(status, can) : null;
  const dismiss = (key: string) => setAlerts((current) => current.filter((a) => a.key !== key));

  return (
    <>
      {notice?.kind === 'banner' && (
        // Não dispensável: some sozinha quando o problema acaba.
        <div className={styles.banner} role="alert" data-testid="printing-banner">
          <PrinterIcon size={16} className={styles.icon} aria-hidden="true" />
          <div className={styles.lines}>
            {notice.lines.map((line) => (
              <span key={line} className={styles.text}>
                {line}
              </span>
            ))}
          </div>
          {notice.link && (
            <Link to={notice.link.to} className={styles.link}>
              {notice.link.label}
            </Link>
          )}
        </div>
      )}

      {notice?.kind === 'paused' && (
        <div className={styles.paused} role="status" data-testid="printing-paused">
          <PauseCircle size={13} aria-hidden="true" />
          <span>{notice.text}</span>
          {notice.link && (
            <Link to={notice.link.to} className={styles.pausedLink}>
              {notice.link.label}
            </Link>
          )}
        </div>
      )}

      {alerts.length > 0 && (
        <ul className={styles.alerts} aria-label="Avisos de impressão">
          {alerts.map((alert) => (
            <li key={alert.key} className={styles.alert} role="alert">
              <span className={styles.alertText}>{alert.text}</span>
              <Link
                to={`/orders?pedido=${encodeURIComponent(alert.orderId)}`}
                className={styles.link}
                onClick={() => dismiss(alert.key)}
              >
                Ver pedido
              </Link>
              <button
                type="button"
                className={styles.dismiss}
                onClick={() => dismiss(alert.key)}
                aria-label="Fechar aviso"
              >
                <X size={14} strokeWidth={1.5} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
